import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';

const POLL_INTERVAL_MS = 3 * 60 * 1000;
const AUTO_RELOAD_AFTER_MS = 30 * 1000;

async function fetchBuildId(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const json = (await res.json()) as { buildId?: string };
    return json.buildId ?? null;
  } catch {
    return null;
  }
}

export function VersionWatcher() {
  const bootBuildIdRef = useRef<string | null>(null);
  const promptedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const checkVersion = async () => {
      if (cancelled || promptedRef.current) return;
      const latest = await fetchBuildId();
      if (!latest) return;

      if (bootBuildIdRef.current == null) {
        bootBuildIdRef.current = latest;
        return;
      }

      if (latest !== bootBuildIdRef.current) {
        promptedRef.current = true;
        toast(
          (t) => (
            <div className="flex items-center gap-3">
              <span>New version available. Reloading in 30s…</span>
              <button
                onClick={() => {
                  toast.dismiss(t.id);
                  window.location.reload();
                }}
                className="px-3 py-1 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
              >
                Reload now
              </button>
            </div>
          ),
          { duration: AUTO_RELOAD_AFTER_MS, id: 'version-update' }
        );
        window.setTimeout(() => {
          window.location.reload();
        }, AUTO_RELOAD_AFTER_MS);
      }
    };

    void checkVersion();
    const interval = window.setInterval(() => void checkVersion(), POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  return null;
}
